
import { Palette, Book, Users, TrendingUp, ArrowRight, FileImage } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type IndustryImage = Tables<'industry_images'>;

const PublishingPage = () => {
  const [industryImages, setIndustryImages] = useState<IndustryImage[]>([]);

  useEffect(() => {
    const fetchIndustryImages = async () => {
      const { data, error } = await supabase
        .from('industry_images')
        .select('*')
        .eq('industry', 'publishing')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching industry images:', error);
      } else {
        setIndustryImages(data || []);
      }
    };

    fetchIndustryImages();
  }, []);

  const industry = {
    title: "Publishing",
    icon: Palette,
    gradient: "from-emerald-500 to-green-500",
    description: "Generate book covers, illustrations, and editorial graphics efficiently while maintaining artistic quality and market appeal.",
    stats: "80% cost reduction"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Book className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Book Covers</h3>
          <p className="text-gray-300 text-sm">Eye-catching designs that sell</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <FileImage className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Editorial Graphics</h3>
          <p className="text-gray-300 text-sm">Professional illustrations and layouts</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Market Impact</h3>
          <p className="text-gray-300 text-sm">Higher sales and reader engagement</p>
        </CardContent>
      </Card>
    </div>
  );

  const imageGallery = (
    <div>
      <h3 className="text-2xl font-bold text-white text-center mb-8">AI-Generated Publishing Examples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {industryImages.length > 0 ? (
          industryImages.map((image) => (
            <Card key={image.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
              <div className="aspect-[3/4] relative overflow-hidden">
                <img 
                  src={image.image_url} 
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgZmlsbD0iIzEwOWM2OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNDAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UHVibGlzaGluZzwvdGV4dD48dGV4dCB4PSI1MCUiIHk9IjYwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkV4YW1wbGU8L3RleHQ+PC9zdmc+';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-300">{image.title}</p>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-3 text-center text-gray-400 py-12">
            <Palette className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">No publishing examples available yet</p>
            <p className="text-sm text-gray-400 mt-2">Images will be added soon</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      heroContent={heroContent}
      mainContent={
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-6">Transforming Publishing Graphics</h2>
          
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              The publishing industry relies heavily on visual appeal to attract readers and drive sales. AI-generated imagery is revolutionizing how publishers create book covers, illustrations, and marketing materials, enabling faster production and lower costs.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The Publishing Challenge</h3>
            <p>
              Publishers face tight budgets, quick turnaround times, and the need to create visually compelling content that stands out in crowded marketplaces, whether digital or physical bookstores.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI-Powered Publishing Solutions</h3>
            <ul className="list-disc list-inside space-y-3 text-gray-300">
              <li><strong className="text-white">Book Cover Design:</strong> Create stunning covers that capture genre and mood</li>
              <li><strong className="text-white">Interior Illustrations:</strong> Generate chapter headers and decorative elements</li>
              <li><strong className="text-white">Marketing Materials:</strong> Design promotional graphics and social media content</li>
              <li><strong className="text-white">Series Consistency:</strong> Maintain visual themes across book series</li>
              <li><strong className="text-white">Rapid Prototyping:</strong> Test different design concepts quickly</li>
            </ul>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Industry Impact</h3>
            <p>
              Publishers using AI for graphic design report 80% reduction in design costs, 60% faster time-to-market, and 25% higher sales due to more compelling visual content.
            </p>
            
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-6 mt-8">
              <h4 className="text-xl font-semibold text-emerald-400 mb-3 flex items-center">
                <Palette className="w-5 h-5 mr-2" />
                Enhance Your Publishing Visuals
              </h4>
              <p className="text-gray-300">
                Create compelling book covers and editorial graphics that capture readers' attention and drive sales in competitive markets.
              </p>
            </div>
          </div>
        </div>
      }
      imageGallery={imageGallery}
    />
  );
};

export default PublishingPage;
